/* eslint-disable @angular-eslint/prefer-inject */
import { Receipt } from '@foundation/receipts/models';
import { ReceiptsRepository } from '@foundation/receipts/state';
import { DownloadButtonComponent } from '@foundation/files/ui';
import { AccessService } from '@foundation/shared/access';
import { BehaviorType, RepositoryTableComponent, TablePaginatorComponent } from '@foundation/table/ui';
import { TranslateDirective, TranslatePipe } from '@foundation/translations/services';
import { FullSpanRowDirective } from '@foundation/utils';
import { CdkMenu, CdkMenuItem, CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import { Attribute, ChangeDetectionStrategy, Component, TemplateRef, inject, input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { switchMap } from 'rxjs';

@Component({
	selector: 'lib-receipt-table',
	standalone: true,
	imports: [
		TablePaginatorComponent,
		DownloadButtonComponent,
		//
		CommonModule,
		TranslateDirective,
		TranslatePipe,
		FullSpanRowDirective,
		ReactiveFormsModule,
		FormsModule,
		CdkMenuModule,
		CdkMenu,
		CdkMenuItem,
	],
	templateUrl: './receipt-table.component.html',
	styleUrl: './receipt-table.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiptTableComponent extends RepositoryTableComponent<Receipt, ReceiptsRepository> {
	/**
	 * Hides the per-row actions, for a table someone may read but not change.
	 *
	 * The billing page shows a customer their own receipts, which they hold read access on and
	 * nothing more; offering rename, delete and share there would present buttons the server is
	 * going to refuse. Issuing and editing happens in the admin dashboard.
	 */
	readonly = input(false);

	/**
	 * What an opened row shows, supplied by the page that mounts the table.
	 *
	 * The table knows how to list a document; it has no business knowing what a given page
	 * lets someone do with one. The row is handed back as `$implicit`.
	 */
	expandedRow = input<TemplateRef<{ $implicit: Receipt }> | null>(null);

	private _accessService = inject(AccessService);
	
	constructor(
		private _repository: ReceiptsRepository,
		@Attribute('click-behavior') clickBehavior: BehaviorType,
		@Attribute('bypass-acls') bypassAclsAttr: string | null
	) {
		// A customer sees the documents they hold read on. The admin section sees every one, which
		// the read endpoint allows only for an admin and refuses to everyone else.
		const bypassAcls = bypassAclsAttr === '' || bypassAclsAttr === 'true';
		super(
			_repository,
			{
				// Issue order, not creation order: the number is the thing a reader scans for, and
				// a draft has none yet.
				orderingBy: { fieldName: 'time_created', direction: 'desc' },
				alwaysOnFilters: [],
				requestFn: (page, pageSize, filters, orderingBy, forceRequest) =>
					_repository.store.getObjects$(page, pageSize, filters, orderingBy, forceRequest, true, bypassAcls),
			},
			clickBehavior
		);
	}

	private _i18n_renameSentence = this._translationService.prep('Give a new name to this receipt:');
	public renameReceipt(receipt: Receipt) {
		this._notificationService
			.prompt(this._i18n_renameSentence(), undefined, {
				defaultValue: receipt.name ?? '',
			})
			.closed.subscribe((promptResult) => {
				if (!promptResult || !promptResult.value) return;
				console.log('You want to rename this receipt:', receipt, 'to', promptResult.value);

				this._repository.store
					.putObject$({ ...receipt, name: promptResult.value })
					.pipe(switchMap(() => this.paginator.refresh()))
					.subscribe();
			});
	}

	private _i18n_deleteSentence = this._translationService.prep('Are you sure you want to delete this receipt?');
	public deleteReceipt(receipt: Receipt) {
		this._notificationService.confirm(this._i18n_deleteSentence()).closed.subscribe((confirmed) => {
			if (!confirmed) return;
			console.log('You want to delete this:', receipt);
			this._repository.store
				.deleteObject$(receipt.id)
				.pipe(switchMap(() => this.paginator.refresh()))
				.subscribe();
		});
	}


	public shareWithTeam(receipt: Receipt) {
		this._accessService.shareWithTeam(receipt.id, 'receipt');
	}

	public openSharingDetails(receipt: Receipt) {
		this._accessService.openSharingDetails(receipt.id, 'receipt');
	}
}
